import type { LifecycleSegmentModel } from '@/shared/generated'
import type { LucideIcon } from 'lucide-react'

import {
	ActivityIcon,
	HelpCircleIcon,
	MoonIcon,
	SproutIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { InterpretationCard } from './InterpretationCard'

type StageMeta = {
	badge: 'success' | 'info' | 'neutral' | 'warning'
	Icon: LucideIcon
	iconClass: string
}

const STAGE_META: Record<LifecycleSegmentModel['stage'], StageMeta> = {
	active: { badge: 'success', Icon: ActivityIcon, iconClass: 'text-success' },
	new: {
		badge: 'info',
		Icon: SproutIcon,
		iconClass: 'text-sky-600 dark:text-sky-400',
	},
	inactive: {
		badge: 'warning',
		Icon: MoonIcon,
		iconClass: 'text-orange-600 dark:text-orange-400',
	},
}

const UNKNOWN_STAGE_META: StageMeta = {
	badge: 'neutral',
	Icon: HelpCircleIcon,
	iconClass: 'text-muted-foreground',
}

const getStageMeta = (stage: LifecycleSegmentModel['stage']): StageMeta =>
	STAGE_META[stage] ?? UNKNOWN_STAGE_META

// Human fallbacks for the i18n defaults; Crowdin owns the final copy.
const STAGE_LABEL: Record<string, string> = {
	active: 'Active',
	new: 'New',
	inactive: 'Inactive',
}

type StageReason = {
	key: string
	defaultValue: string
	defaultValueOne?: string
}

const STAGE_REASON: Record<string, StageReason> = {
	active: {
		key: 'midterm_forecast.lifecycle.reason.active',
		defaultValue: 'Steady sales history with no decline trend. Mature stage.',
	},
	new: {
		key: 'midterm_forecast.lifecycle.reason.new',
		defaultValue:
			'Only {weeks} weeks of sales — still in the New stage, no established trajectory.',
		defaultValueOne:
			'Only {weeks} week of sales — still in the New stage, no established trajectory.',
	},
	inactive: {
		key: 'midterm_forecast.lifecycle.reason.inactive',
		defaultValue: 'No recent sales — the pricing line has gone dormant.',
	},
}

const UNKNOWN_STAGE_REASON: StageReason = {
	key: 'midterm_forecast.lifecycle.reason.unknown',
	defaultValue: 'Lifecycle stage could not be determined.',
}

export const LifecycleCard = ({
	lifecycle,
	blocking,
	isPending,
	isError,
}: {
	lifecycle: LifecycleSegmentModel | null | undefined
	blocking: boolean
	isPending: boolean
	isError: boolean
}) => {
	const { t } = useTranslation()

	if (isPending) {
		return (
			<Skeleton className='h-16 w-full' data-testid='mtf-lifecycle-skeleton' />
		)
	}

	if (isError) {
		return (
			<p
				className='rounded-lg border p-3 text-sm text-muted-foreground'
				data-testid='mtf-lifecycle-error'
			>
				{t('midterm_forecast.lifecycle.error', {
					defaultValue: 'Failed to load the lifecycle stage.',
				})}
			</p>
		)
	}

	if (!lifecycle) {
		return (
			<p
				className='rounded-lg border p-3 text-sm text-muted-foreground'
				data-testid='mtf-lifecycle-empty'
			>
				{t('midterm_forecast.lifecycle.empty', {
					defaultValue: 'No lifecycle data for this pricing line yet.',
				})}
			</p>
		)
	}

	const { badge, Icon, iconClass } = getStageMeta(lifecycle.stage)
	const stageLabel =
		lifecycle.stage in STAGE_LABEL
			? t(`midterm_forecast.lifecycle.stage.${lifecycle.stage}`, {
					defaultValue: STAGE_LABEL[lifecycle.stage],
				})
			: t('midterm_forecast.lifecycle.stage.unknown', {
					defaultValue: 'Unknown',
				})
	const reasonEntry = STAGE_REASON[lifecycle.stage] ?? UNKNOWN_STAGE_REASON
	const reason = t(reasonEntry.key, {
		defaultValue: reasonEntry.defaultValue,
		defaultValue_one: reasonEntry.defaultValueOne,
		count: lifecycle.weeks_live,
		weeks: lifecycle.weeks_live,
	})

	return (
		<InterpretationCard
			icon={<Icon className={cn('size-4', iconClass)} />}
			title={t('midterm_forecast.lifecycle.title', {
				defaultValue: 'Lifecycle',
			})}
			badge={
				<Badge variant={badge} data-testid='mtf-lifecycle-badge'>
					{stageLabel}
				</Badge>
			}
			blocking={blocking}
			blockingTestId='mtf-lifecycle-blocking'
			toggleTestId='mtf-lifecycle-toggle'
			data-testid='mtf-lifecycle-card'
		>
			<p className='text-xs text-muted-foreground'>{reason}</p>
			<div className='grid grid-cols-2 gap-2'>
				<div
					className='rounded-lg bg-secondary/50 p-2.5'
					data-testid='mtf-lifecycle-stage'
				>
					<p className='text-xs text-muted-foreground'>
						{t('midterm_forecast.lifecycle.tile_stage', {
							defaultValue: 'Stage',
						})}
					</p>
					<p className='text-sm font-bold tabular-nums'>{stageLabel}</p>
				</div>
				<div
					className='rounded-lg bg-secondary/50 p-2.5'
					data-testid='mtf-lifecycle-months'
				>
					<p className='text-xs text-muted-foreground'>
						{t('midterm_forecast.lifecycle.tile_months', {
							defaultValue: 'Months live',
						})}
					</p>
					<p className='text-sm font-bold tabular-nums'>
						{formatNumber(lifecycle.months_live, NumberFormats.Integer)}
					</p>
					<p className='text-xs text-muted-foreground tabular-nums'>
						{t('midterm_forecast.lifecycle.days_live', {
							defaultValue: '{days} days',
							defaultValue_one: '{days} day',
							count: lifecycle.days_live,
							days: formatNumber(lifecycle.days_live, NumberFormats.Integer),
						})}
					</p>
				</div>
			</div>
		</InterpretationCard>
	)
}
